import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import type { UpdateStatus } from '@vidmakr/shared/desktop';
import { desktop } from '@/lib/desktop';
import { Button } from './ui';

/** Live auto-update status from the desktop app (undefined outside it). */
export function useDesktopUpdate(): UpdateStatus | undefined {
  const [status, setStatus] = useState<UpdateStatus | undefined>(undefined);
  useEffect(() => {
    if (!desktop) return;
    let alive = true;
    void desktop.getUpdateStatus().then((s) => alive && setStatus(s));
    const off = desktop.onUpdateStatus(setStatus);
    return () => {
      alive = false;
      off();
    };
  }, []);
  return status;
}

/** "Restart to update" prompt, shown only in the desktop app once an update is downloaded. */
export function DesktopUpdateBanner() {
  const status = useDesktopUpdate();
  const [dismissed, setDismissed] = useState('');
  if (!desktop || status?.state !== 'downloaded' || dismissed === status.version) return null;
  return (
    <div role="status" className="fixed bottom-4 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-3 rounded-xl border border-line bg-panel-2 py-2 pr-2 pl-4 text-sm shadow-2xl">
      <RefreshCw size={15} className="shrink-0 text-accent-2" />
      <span>
        vidmakr {status.version} is ready. <span className="text-muted">Restart to update.</span>
      </span>
      <Button variant="primary" size="sm" onClick={() => void desktop!.installUpdate()}>
        Restart
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Later" title="Later (installs when you quit)" onClick={() => setDismissed(status.version ?? '')}>
        <X size={14} />
      </Button>
    </div>
  );
}
